import { css } from '@emotion/react';
import type { Placement } from '@floating-ui/react';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { useMatchMedia } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import {
  PopoverMenu,
  PopoverMenuItem,
  Text,
  Truncated,
  usePopoverMenu,
  NakedButton,
  useTooltip,
} from '@frontend/design-system';
import { useBookableAppointmentTypes } from '../../hooks/scheduler-v3/use-bookable-appointment-types';
import { ColorDot } from '../appointment-panel/color-dot';

/** Converts an ISO 8601 duration (e.g. "PT1H30M", "PT45M") to total minutes. */
export const iso8601ToMinutes = (iso: string): number => {
  const hours = parseInt(iso.match(/(\d+)H/)?.[1] ?? '0', 10);
  const minutes = parseInt(iso.match(/(\d+)M/)?.[1] ?? '0', 10);
  return hours * 60 + minutes;
};

type Props = {
  locationId: string;
  selectedId?: string;
  onSelect: (appointmentTypeId: string) => void;
  placement?: Placement;
  leadingIcon?: IconName;
  useNameLabel?: boolean;
};

export const BookableServiceSelector = ({
  locationId,
  selectedId,
  onSelect,
  placement = 'bottom-start',
  leadingIcon = undefined,
  useNameLabel = true,
}: Props) => {
  const { t } = useTranslation('schedule');
  const isMedium = useMatchMedia({ maxWidth: 1160 });

  const { bookableAppointmentTypes } = useBookableAppointmentTypes(locationId);
  const resolvedId = selectedId ?? bookableAppointmentTypes[0]?.id;
  const selectedAppointmentType = bookableAppointmentTypes.find((a) => a.id === resolvedId);
  const selectedLabelName = selectedAppointmentType?.name ?? '';
  const selectedLabelDuration = selectedAppointmentType?.durationMinutes ?? null;

  const { getTriggerProps, getMenuProps, getItemProps } = usePopoverMenu({ placement });
  const isOpen = getMenuProps().isOpen;

  const { Tooltip, triggerProps: tooltipTriggerProps, tooltipProps } = useTooltip({ trigger: 'hover' });

  if (bookableAppointmentTypes.length === 0) return null;

  return (
    <>
      <NakedButton
        css={[
          appointmentTypeSelectorStyles.trigger,
          { padding: isMedium ? theme.spacing(1, 1.5) : theme.spacing(0.5, 1.5) },
        ]}
        {...getTriggerProps()}
      >
        {leadingIcon && (
          <span {...(!useNameLabel && isMedium ? tooltipTriggerProps : {})}>
            <Icon name={leadingIcon} size={16} color='light' />
          </span>
        )}
        {useNameLabel ? (
          <>
            {selectedAppointmentType?.color && (
              <ColorDot color={selectedAppointmentType.color} size='medium' style={{ flex: '0 0 auto' }} />
            )}
            <Truncated as={Text} size='medium' weight='bold' color='default' css={{ maxWidth: 120 }}>
              {selectedLabelName}
            </Truncated>
            {selectedLabelDuration && (
              <Text as='span' size='small' weight='light' color='subdued' css={{ marginLeft: theme.spacing(1) }}>
                {t('{{count}} min', { count: selectedLabelDuration })}
              </Text>
            )}
          </>
        ) : (
          <>
            {!isMedium && (
              <Text size='medium' weight='bold' color='default'>
                {t('Find Availability')}
              </Text>
            )}
          </>
        )}
        <Icon
          name='caret-down-tiny'
          size={8}
          color='light'
          css={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </NakedButton>
      {!useNameLabel && isMedium && <Tooltip {...tooltipProps}>{t('Find Availability')}</Tooltip>}
      <PopoverMenu {...getMenuProps()}>
        {bookableAppointmentTypes.map((apptType, index) => {
          const durationMins = apptType.durationMinutes ?? null;
          const appointmentColor = apptType.color ?? undefined;
          const isSelected = apptType.id === resolvedId;

          return (
            <PopoverMenuItem
              key={apptType.id}
              {...getItemProps({
                index,
                onClick: () => {
                  if (apptType.id) onSelect(apptType.id);
                },
              })}
              css={styles.popoverItem(isSelected)}
            >
              <div css={styles.menuItem}>
                <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
                  {appointmentColor && <ColorDot color={appointmentColor} size='medium' style={{ flex: '0 0 auto' }} />}
                  <Text>{apptType.name}</Text>
                </div>
                {durationMins !== null && (
                  <Text size='small' color='light' css={{ fontStyle: 'italic', textAlign: 'right' }}>
                    {t('{{count}} min', { count: durationMins })}
                  </Text>
                )}
              </div>
            </PopoverMenuItem>
          );
        })}
      </PopoverMenu>
    </>
  );
};

export const appointmentTypeSelectorStyles = {
  trigger: css`
    display: flex;
    align-items: center;
    white-space: nowrap;
    gap: ${theme.spacing(0.5)};
    border-radius: ${theme.borderRadius.small};
    background-color: ${theme.colors.primary5};
    border: 1px solid ${theme.colors.primary40};
    height: 32px;
    box-sizing: border-box;
  `,
};

const styles = {
  menuItem: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: ${theme.spacing(3)};
  `,
  popoverItem: (isSelected: boolean) =>
    isSelected &&
    css`
      background-color: var(--colors-primary-10);
    `,
};
